/**
 * Preloads images in the browser by creating HTMLImageElement instances.
 *
 * This function takes an array of image URLs and triggers the browser to load them.
 * It resolves once all images have finished loading or rejects if any fail.
 * This is useful for pre-warming image cache before displaying them in the UI.
 *
 * @function
 * @async
 * @param {string[]} urls - An array of image URLs to preload in the browser.
 * 
 * @example
 * await preloadImages([
 *   "http://localhost:3000/drive/proxyImage/abc123",
 *   "http://localhost:3000/drive/proxyImage/def456"
 * ]);
 *
 * @returns {Promise<void>} A promise that resolves when all images have been loaded.
 */
const preloadImages = async (urls = []) => {
  const unique = Array.from(new Set(urls.filter(Boolean)));

  const tasks = unique.map(url => new Promise((resolve) => {
    const img = new Image();
    img.onload = async () => {
      try {
        if ('decode' in img) await img.decode();
      } catch (_) {}
      resolve();
    };
    img.onerror = () => resolve(); // don’t block on errors
    img.src = url;
  }));

  await Promise.all(tasks);
};
export default preloadImages;